/**
 * Stale Offer Reminders (MRG-389) — sends, opens, clicks, and disposition
 * snapshots by reminder day (3, 7, 14).
 *
 * Route: /admin/reporting/stale-offer-reminders
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { DatePicker } from '@/common/components/date-picker'
import {
  useStaleOfferReminders,
  useStaleOfferReminderRecords,
} from '@/features/reporting/hooks/useReportingData'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { series, chrome, semantic, table, tooltipStyle, tickProps } from '@/features/reporting/chartPalette'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: StaleOfferRemindersPage,
})

type Granularity = 'day' | 'week' | 'month'
type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function formatPeriodLabel(period: string): string {
  const weekMatch = period.match(/^(\d{4})-W(\d{2})$/)
  if (weekMatch) {
    const year = Number(weekMatch[1])
    const week = Number(weekMatch[2])
    const jan4 = new Date(year, 0, 4)
    const dayOfWeek = jan4.getDay() || 7
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7)
    return format(monday, 'MMM d')
  }
  const monthMatch = period.match(/^(\d{4})-(\d{2})$/)
  if (monthMatch) {
    const d = new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1)
    return format(d, "MMM ''yy")
  }
  const dayMatch = period.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dayMatch) {
    const d = new Date(Number(dayMatch[1]), Number(dayMatch[2]) - 1, Number(dayMatch[3]))
    return format(d, 'MMM d')
  }
  return period
}

function parseDateAsLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function StaleOfferRemindersPage() {
  const [granularity, setGranularity] = useState<Granularity>('week')
  const [drillDownPeriod, setDrillDownPeriod] = useState<string | null>(null)
  const [drillDownPage, setDrillDownPage] = useState(1)
  const [dateRange, setDateRange] = useState<DateRangePreset>('3mo')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 3), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  useEffect(() => {
    setDrillDownPeriod(null)
  }, [granularity, dateRange])

  useEffect(() => {
    setDrillDownPage(1)
  }, [drillDownPeriod])

  const { data: aggregateRes } = useStaleOfferReminders(granularity, startDateStr, endDateStr)
  const { data: recordsRes, isFetching: isRecordsFetching } = useStaleOfferReminderRecords(
    drillDownPeriod, granularity, startDateStr, endDateStr, drillDownPage, 25,
  )

  const handleChartClick = useCallback(
    (nextState: { activeLabel?: string | number | undefined }) => {
      if (nextState.activeLabel != null) {
        setDrillDownPeriod(String(nextState.activeLabel))
      }
    },
    [],
  )

  const totals = useMemo(() => {
    const data = aggregateRes?.data ?? []
    const sum = (key: keyof (typeof data)[number]) =>
      data.reduce((s, d) => s + (Number(d[key]) || 0), 0)
    const day3 = sum('day_3_sent')
    const day7 = sum('day_7_sent')
    const day14 = sum('day_14_sent')
    const total = sum('total_sent')
    const opened = sum('day_3_opened') + sum('day_7_opened') + sum('day_14_opened')
    const clicked = sum('day_3_clicked') + sum('day_7_clicked') + sum('day_14_clicked')
    // Actioned = any non-pending status hit within attribution window.
    const day3Actioned =
      sum('day_3_accepted') + sum('day_3_rejected') + sum('day_3_withdrawn') + sum('day_3_engaged')
    const day7Actioned =
      sum('day_7_accepted') + sum('day_7_rejected') + sum('day_7_withdrawn') + sum('day_7_engaged')
    const day14Actioned =
      sum('day_14_accepted') +
      sum('day_14_rejected') +
      sum('day_14_withdrawn') +
      sum('day_14_engaged')
    // Denominator for the actioned-rate metric is "opened sends with a parseable
    // property_id" (the only sends that can possibly attribute) — that's the sum
    // of all 5 transition buckets for the day. Using `day_N_sent` here would
    // mix open-gated and ungated populations and understate effectiveness.
    const day3Attributable = day3Actioned + sum('day_3_still_pending')
    const day7Attributable = day7Actioned + sum('day_7_still_pending')
    const day14Attributable = day14Actioned + sum('day_14_still_pending')
    return {
      day3, day7, day14, total, opened, clicked,
      day3Actioned, day7Actioned, day14Actioned,
      day3Attributable, day7Attributable, day14Attributable,
    }
  }, [aggregateRes])

  // Per-day transition aggregates for the outcomes chart. Each row of the
  // existing time-series gets collapsed into per-day-bucket totals so the
  // chart is single-bar-per-bucket (rather than per-period).
  const transitionsByDay = useMemo(() => {
    const data = aggregateRes?.data ?? []
    const sum = (key: keyof (typeof data)[number]) =>
      data.reduce((s, d) => s + (Number(d[key]) || 0), 0)
    const window = aggregateRes?.attribution_window_days ?? { '3': 4, '7': 7, '14': 7 }
    return [
      {
        day: 'Day 3',
        windowDays: window['3'] ?? 4,
        accepted: sum('day_3_accepted'),
        rejected: sum('day_3_rejected'),
        withdrawn: sum('day_3_withdrawn'),
        engaged: sum('day_3_engaged'),
        still_pending: sum('day_3_still_pending'),
      },
      {
        day: 'Day 7',
        windowDays: window['7'] ?? 7,
        accepted: sum('day_7_accepted'),
        rejected: sum('day_7_rejected'),
        withdrawn: sum('day_7_withdrawn'),
        engaged: sum('day_7_engaged'),
        still_pending: sum('day_7_still_pending'),
      },
      {
        day: 'Day 14',
        windowDays: window['14'] ?? 7,
        accepted: sum('day_14_accepted'),
        rejected: sum('day_14_rejected'),
        withdrawn: sum('day_14_withdrawn'),
        engaged: sum('day_14_engaged'),
        still_pending: sum('day_14_still_pending'),
      },
    ]
  }, [aggregateRes])

  const formatActionedPct = (actioned: number, sent: number) =>
    sent > 0 ? `${Math.round((actioned / sent) * 100)}%` : '–'

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Stack gap="4" style={{ marginBottom: '12px' }}>
          <Text as="h1" variant="h4" weight="bold">Stale Offer Reminders</Text>
          <Text variant="bodySm" color="muted">
            Per-property nudges sent at days 3, 7, and 14 after the earliest pending offer
            (MRG-389). Click a bar to drill into individual sends.
          </Text>
        </Stack>

        <div className={styles.controls}>
          <div className={styles.granularityToggle}>
            <button className={clsx(styles.granularityButton, granularity === 'month' && styles.granularityButtonActive)} onClick={() => setGranularity('month')}>Monthly</button>
            <button className={clsx(styles.granularityButton, granularity === 'week' && styles.granularityButtonActive)} onClick={() => setGranularity('week')}>Weekly</button>
            <button className={clsx(styles.granularityButton, granularity === 'day' && styles.granularityButtonActive)} onClick={() => setGranularity('day')}>Daily</button>
          </div>
          <div className={styles.controlDateGroup}>
            <div className={styles.granularityToggle}>
              {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
                <button key={preset} className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)} onClick={() => handleDateRangeChange(preset)}>
                  {preset === 'custom' ? 'Custom' : preset}
                </button>
              ))}
            </div>
            <div className={styles.customDateRange}>
              <div className={styles.customDatePicker}>
                <DatePicker label="Start" value={parseDateAsLocal(startDateStr)} onChange={setCustomStart} maxDate={parseDateAsLocal(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
              </div>
              <Text variant="bodySm" color="muted">{'–'}</Text>
              <div className={styles.customDatePicker}>
                <DatePicker label="End" value={parseDateAsLocal(endDateStr)} onChange={setCustomEnd} minDate={parseDateAsLocal(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <Text variant="bodySm" color="muted">Total Sent</Text>
            <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{formatCount(totals.total)}</Text>
            <Text variant="bodyXs" color="muted">across all reminder days</Text>
          </div>
          <div className={styles.metricCard}>
            <Text variant="bodySm" color="muted">Day 3</Text>
            <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{formatCount(totals.day3)}</Text>
            <Text variant="bodyXs" color="muted">
              {formatCount(totals.day3Actioned)} actioned ({formatActionedPct(totals.day3Actioned, totals.day3Attributable)} of opened)
            </Text>
          </div>
          <div className={styles.metricCard}>
            <Text variant="bodySm" color="muted">Day 7</Text>
            <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{formatCount(totals.day7)}</Text>
            <Text variant="bodyXs" color="muted">
              {formatCount(totals.day7Actioned)} actioned ({formatActionedPct(totals.day7Actioned, totals.day7Attributable)} of opened)
            </Text>
          </div>
          <div className={styles.metricCard}>
            <Text variant="bodySm" color="muted">Day 14</Text>
            <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{formatCount(totals.day14)}</Text>
            <Text variant="bodyXs" color="muted">
              {formatCount(totals.day14Actioned)} actioned ({formatActionedPct(totals.day14Actioned, totals.day14Attributable)} of opened)
            </Text>
          </div>
        </div>

        <div className={styles.chartContainer}>
          <div className={styles.chartHeader}>
            <Text variant="bodySm" color="muted">Sends by reminder day</Text>
            <ExportMenu options={[
              { label: 'Chart Data (CSV)', data: (aggregateRes?.data ?? []) as unknown as Record<string, unknown>[], filename: 'stale-offer-reminders.csv' },
            ]} />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={aggregateRes?.data ?? []} onClick={handleChartClick}>
              <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
              <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
              <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} />
              <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="day_3_sent" name="Day 3" stackId="reminders" fill={series.primary} cursor="pointer" isAnimationActive={false} />
              <Bar dataKey="day_7_sent" name="Day 7" stackId="reminders" fill={series.teal} cursor="pointer" isAnimationActive={false} />
              <Bar dataKey="day_14_sent" name="Day 14" stackId="reminders" fill={series.accent1} radius={[4, 4, 0, 0]} cursor="pointer" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={styles.chartContainer}>
          <div className={styles.chartHeader}>
            <Text variant="bodySm" color="muted">
              Offer outcomes for opened sends within attribution window
              {' '}
              <span style={{ color: chrome.axisLine }}>
                (Day 3: {transitionsByDay[0]?.windowDays ?? 4}d, Day 7: {transitionsByDay[1]?.windowDays ?? 7}d, Day 14: {transitionsByDay[2]?.windowDays ?? 7}d)
              </span>
            </Text>
            <ExportMenu options={[
              { label: 'Outcomes (CSV)', data: transitionsByDay as unknown as Record<string, unknown>[], filename: 'stale-offer-reminders-outcomes.csv' },
            ]} />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={transitionsByDay} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} horizontal={false} />
              <XAxis type="number" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} />
              <YAxis type="category" dataKey="day" tick={tickProps} tickLine={false} axisLine={false} width={70} />
              <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="accepted" name="Accepted" stackId="outcomes" fill={semantic.positive} isAnimationActive={false} />
              <Bar dataKey="rejected" name="Rejected" stackId="outcomes" fill={semantic.negative} isAnimationActive={false} />
              <Bar dataKey="withdrawn" name="Withdrawn" stackId="outcomes" fill={series.accent2} isAnimationActive={false} />
              <Bar dataKey="engaged" name="Engaged" stackId="outcomes" fill={series.primary} isAnimationActive={false} />
              <Bar dataKey="still_pending" name="Still pending" stackId="outcomes" fill={chrome.gridStroke} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
          <Text variant="bodyXs" color="muted" style={{ marginTop: '8px' }}>
            A send is credited with an outcome only if the agent opened the email and an
            offer on the flagged property has <code>updated_at</code> after the open and
            within the attribution window. Highest-priority hit wins: accepted &gt;
            rejected &gt; withdrawn &gt; engaged &gt; still pending.
          </Text>
        </div>

        {drillDownPeriod && (
          <div className={styles.drillDownPanel}>
            <div className={styles.drillDownHeader}>
              <Text variant="body" weight="bold">
                Sends in {formatPeriodLabel(drillDownPeriod)}
              </Text>
              <Button variant="ghost" size="sm" onClick={() => setDrillDownPeriod(null)}>
                <Icon name="close" size={18} />
              </Button>
            </div>
            <div style={{ position: 'relative', minHeight: '300px' }}>
              {(!recordsRes || isRecordsFetching) && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {recordsRes?.data && recordsRes.data.length > 0 && (
                <>
                  <table className={styles.recordsTable}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Sent</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Day</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Property</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Agent</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Active @ send</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Active now</th>
                        <th style={{ textAlign: 'center', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Accepted?</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Status now</th>
                        <th style={{ textAlign: 'center', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Opened</th>
                        <th style={{ textAlign: 'center', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Clicked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recordsRes.data.map((r) => (
                        <tr key={r.id}>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>
                            {r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>Day {r.reminder_day}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{r.property_address ?? '-'}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{r.agent_name ?? r.recipient_email}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{r.pending_offer_count_at_send}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{r.current_active_offer_count}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'center' }}>
                            {r.has_accepted_offer ? (
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', backgroundColor: '#D1FAE5', color: '#065F46', fontSize: '11px', fontWeight: 600 }}>Yes</span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textTransform: 'capitalize' }}>{r.current_property_status?.replace(/_/g, ' ') ?? '-'}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'center' }}>{r.opened ? 'Yes' : '-'}</td>
                          <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'center' }}>{r.clicked ? 'Yes' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {recordsRes.pagination && (
                    <div className={styles.paginationControls}>
                      <Text variant="bodyXs" color="muted">
                        Page {recordsRes.pagination.page} of {recordsRes.pagination.total_pages} ({recordsRes.pagination.total_count} records)
                      </Text>
                      <Stack direction="row" gap="8">
                        <Button variant="outline" size="sm" disabled={isRecordsFetching || recordsRes.pagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={isRecordsFetching || !recordsRes.pagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                      </Stack>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
